import { useState, useEffect } from 'react';

interface CountdownTimerProps {
  endDate: string;
  timezone: string;
  labels: {
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
  };
  textColor: string;
  accentColor: string;
  onExpired?: () => void;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

export function CountdownTimer({ 
  endDate, 
  timezone, 
  labels, 
  textColor, 
  accentColor,
  onExpired 
}: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(endDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true
        });
        
        if (onExpired) {
          onExpired();
        }
        
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
        isExpired: false
      });
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [endDate, onExpired]);

  if (timeLeft.isExpired) {
    return (
      <div 
        className="text-xs sm:text-sm font-bold px-3 py-1 bg-white/10 rounded-lg border border-white/30"
        style={{ color: textColor }}
      >
        Offer Expired
      </div>
    );
  }

  const timeUnits = [
    { value: timeLeft.days, label: labels.days || 'd' },
    { value: timeLeft.hours, label: labels.hours || 'h' },
    { value: timeLeft.minutes, label: labels.minutes || 'm' },
    { value: timeLeft.seconds, label: labels.seconds || 's' }
  ];

  return (
    <div className="flex items-center gap-1.5 sm:gap-2">
      {timeUnits.map((unit, index) => (
        <div 
          key={index}
          className="flex items-baseline gap-0.5"
        >
          <div 
            className="relative px-1.5 sm:px-2 py-0.5 sm:py-1 rounded backdrop-blur-sm"
            style={{
              background: `linear-gradient(135deg, ${accentColor}15, ${accentColor}25)`,
              border: `1px solid ${accentColor}40`
            }}
          >
            <span 
              className="text-sm sm:text-lg font-bold tabular-nums"
              style={{ color: textColor }}
            >
              {unit.value.toString().padStart(2, '0')}
            </span>
          </div>
          <span 
            className="text-[10px] sm:text-xs font-medium ml-0.5"
            style={{ color: textColor, opacity: 0.8 }}
          >
            {unit.label}
          </span>
          {index < timeUnits.length - 1 && (
            <span 
              className="text-xs sm:text-sm mx-0.5 font-bold"
              style={{ color: textColor, opacity: 0.5 }}
            >
              :
            </span>
          )}
        </div>
      ))}
    </div>
  );
}